import React, { useEffect, useMemo, useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  DatePicker,
  Button,
  Space,
  Typography,
  message,
  theme,
} from 'antd';
import { SaveOutlined, ToolOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useRequest from 'hooks/useRequest';
import { useCliente } from 'providers/Clientes/ClienteProvider';
import { useColeccion } from 'providers/ColeccionProvider';
import { TIPO_EQUIPO_OPTIONS } from './tipoEquipoCategoria';

const { Text } = Typography;
const { useToken } = theme;

// Form de creación/edición de un equipo individual. Usa PUT /clientes/:id/ascensores
// (bulk endpoint) pasando un arreglo de 1 elemento — no hay endpoint de PUT/POST
// individual y crear uno implicaba tocar backend fuera del scope de Fase 2. El
// endpoint bulk inserta los elementos sin `id` y actualiza los que sí lo tienen.
// Si este patrón se repite en más sitios, considerar PUT /ascensores/:id en
// una fase posterior.
//
// Props:
// - equipo: fila existente (edit) o null/undefined (create)
// - idCliente: requerido
// - preselectedEdificioId: si viene, se preselecciona en create
// - requireEdificio: si true, el Select de edificio es obligatorio (default
//   false en edit; true conviene en create para forzar la relación)
// - onSaved(savedEquipo): callback después de persistir
function EquipoDatosForm({
  equipo,
  idCliente,
  preselectedEdificioId,
  requireEdificio = false,
  onSaved,
}) {
  const isCreate = !equipo?.id;
  const [form] = Form.useForm();
  const { token } = useToken();
  const [submitting, setSubmitting] = useState(false);
  const { request } = useRequest();
  const { callAscensores } = useCliente();
  const { findItemsByType } = useColeccion();
  // El select de edificio se carga desde /clientes/:id/edificios. Caché local
  // para no repetir la llamada si el usuario abre y cierra el drawer varias
  // veces durante la misma sesión.
  const [edificiosOptions, setEdificiosOptions] = useState([]);
  const [loadingEdificios, setLoadingEdificios] = useState(false);
  // Plantillas de plan de mantención (coleccion_item type=ACTIVIDAD). Setear
  // id_tipo_ascensor dispara el traspaso de actividades al equipo en backend
  // (routes/clientes/index.ts:875 → cliente.srv.ts:updateMaintanceItemsForAscensor).
  const [plantillas, setPlantillas] = useState([]);
  const [loadingPlantillas, setLoadingPlantillas] = useState(false);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      if (!idCliente) return;
      setLoadingEdificios(true);
      try {
        const data = await request(`/clientes/${idCliente}/edificios`, 'GET');
        if (cancel) return;
        const opts = (Array.isArray(data) ? data : []).map((e) => ({
          value: e.id,
          label: `${e.nombre} · ${e.direccion}`,
        }));
        setEdificiosOptions(opts);
      } finally {
        if (!cancel) setLoadingEdificios(false);
      }
    };
    load();
    return () => {
      cancel = true;
    };
  }, [idCliente, request]);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      setLoadingPlantillas(true);
      try {
        const data = await findItemsByType('ACTIVIDAD');
        if (cancel) return;
        setPlantillas(Array.isArray(data) ? data : []);
      } finally {
        if (!cancel) setLoadingPlantillas(false);
      }
    };
    load();
    return () => {
      cancel = true;
    };
  }, [findItemsByType]);

  useEffect(() => {
    if (isCreate) {
      form.resetFields();
      form.setFieldsValue({
        id_edificio: preselectedEdificioId ?? null,
        tipo_equipo_categoria: 'ascensor_pasajeros',
        estado: 'OPERATIVO',
        id_tipo_ascensor: null,
        requiere_plan: true,
      });
      return;
    }
    form.setFieldsValue({
      id_edificio: equipo.id_edificio ?? null,
      nombre: equipo.nombre ?? '',
      tipo_equipo_categoria: equipo.tipo_equipo_categoria ?? 'ascensor_pasajeros',
      id_tipo_ascensor: equipo.id_tipo_ascensor ?? null,
      // equipo.requiere_plan viene del backend como boolean. Caso undefined
      // se trata como false (mas seguro: si la API aun no expone el campo,
      // mostramos "zona" para no mentir al usuario).
      requiere_plan: equipo.requiere_plan === true || equipo.requiere_plan === 1,
      marca: equipo.marca ?? '',
      modelo: equipo.modelo ?? '',
      fabricante: equipo.fabricante ?? '',
      numero_serie: equipo.numero_serie ?? '',
      num_registro_minvu: equipo.num_registro_minvu ?? '',
      capacidad: equipo.capacidad ?? '',
      pisos: equipo.pisos ?? '',
      ano_fabricacion: equipo.ano_fabricacion ?? null,
      ano_instalacion: equipo.ano_instalacion ?? null,
      velocidad_mpm: equipo.velocidad_mpm ?? null,
      altura_recorrido_m: equipo.altura_recorrido_m ?? null,
      fecha_recepcion_dom: equipo.fecha_recepcion_dom ? dayjs(equipo.fecha_recepcion_dom) : null,
    });
  }, [equipo, form, isCreate, preselectedEdificioId]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const merged = {
        ...(equipo ?? {}),
        ...values,
        fecha_recepcion_dom: values.fecha_recepcion_dom
          ? dayjs(values.fecha_recepcion_dom).format('YYYY-MM-DD')
          : null,
      };
      if (isCreate && 'id' in merged) delete merged.id;
      await request(`/clientes/${idCliente}/ascensores`, 'PUT', {
        ascensores: [merged],
        deletedAscensores: [],
      });
      message.success(isCreate ? 'Equipo creado' : 'Equipo actualizado');
      await callAscensores?.(idCliente);
      onSaved?.(merged);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.mensaje || 'No se pudo guardar el equipo');
    } finally {
      setSubmitting(false);
    }
  };

  const plantillaOptions = useMemo(
    () => plantillas.map((p) => ({ value: p.id, label: p.nombre })),
    [plantillas]
  );

  return (
    <Form form={form} layout="vertical" requiredMark={false}>
      <Space.Compact block>
        <Form.Item
          label="Nombre"
          name="nombre"
          style={{ flex: 1, marginRight: 8 }}
          rules={[
            { required: true, message: 'El nombre es obligatorio' },
            { max: 60, message: 'Máximo 60 caracteres' },
          ]}
        >
          <Input placeholder="Ascensor Principal" />
        </Form.Item>
        <Form.Item
          label="Tipo"
          name="tipo_equipo_categoria"
          style={{ flex: 1 }}
          rules={[{ required: true, message: 'Selecciona el tipo' }]}
        >
          <Select options={TIPO_EQUIPO_OPTIONS} />
        </Form.Item>
      </Space.Compact>

      <div
        style={{
          background: token.colorPrimaryBg,
          border: `1px solid ${token.colorPrimaryBorder}`,
          borderRadius: token.borderRadiusLG,
          padding: token.paddingMD,
          marginBottom: token.marginMD,
        }}
      >
        <Form.Item
          label={
            <Space>
              <ToolOutlined style={{ color: token.colorPrimary }} />
              <Text strong>Requiere plan de mantención</Text>
            </Space>
          }
          name="requiere_plan"
          valuePropName="checked"
          tooltip="Desactivá esto si esta fila es una zona, ubicación, sala de máquinas o cualquier elemento que NO es un equipo real y por lo tanto no necesita plan de mantención."
          style={{ marginBottom: token.marginSM }}
        >
          <Switch checkedChildren="Sí (equipo real)" unCheckedChildren="No (zona/ubicación)" />
        </Form.Item>

        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.requiere_plan !== curr.requiere_plan}>
          {({ getFieldValue }) =>
            getFieldValue('requiere_plan') ? (
              <Form.Item
                label={<Text strong>Plantilla de plan</Text>}
                name="id_tipo_ascensor"
                tooltip="Al asignar una plantilla se copian todas las actividades al equipo. Cambiarla reemplaza el plan actual (las actividades previas quedan inactivas)."
                extra={
                  !isCreate && !equipo?.id_tipo_ascensor ? (
                    <Text type="warning">
                      Este equipo requiere plan pero no tiene plantilla asignada — no entrará en las
                      mantenciones automáticas hasta que elijas una.
                    </Text>
                  ) : null
                }
                style={{ margin: 0 }}
              >
                <Select
                  placeholder="Selecciona la plantilla de mantención"
                  loading={loadingPlantillas}
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={plantillaOptions}
                />
              </Form.Item>
            ) : null
          }
        </Form.Item>
      </div>

      <Form.Item
        label="Edificio"
        name="id_edificio"
        tooltip="Si el edificio aún no existe, créalo primero en el tab 'Edificios'"
        rules={requireEdificio ? [{ required: true, message: 'Selecciona un edificio' }] : []}
      >
        <Select
          allowClear={!requireEdificio}
          loading={loadingEdificios}
          options={edificiosOptions}
          placeholder={requireEdificio ? 'Selecciona el edificio' : 'Sin edificio asignado'}
        />
      </Form.Item>

      <Space.Compact block>
        <Form.Item
          label="Marca"
          name="marca"
          style={{ flex: 1, marginRight: 8 }}
          rules={[{ max: 45, message: 'Máximo 45 caracteres' }]}
        >
          <Input placeholder="Otis" />
        </Form.Item>
        <Form.Item
          label="Modelo"
          name="modelo"
          style={{ flex: 1 }}
          rules={[{ max: 45, message: 'Máximo 45 caracteres' }]}
        >
          <Input placeholder="Gen2" />
        </Form.Item>
      </Space.Compact>

      <Form.Item label="Fabricante" name="fabricante">
        <Input placeholder="Otis Elevator Company" />
      </Form.Item>

      <Space.Compact block>
        <Form.Item label="N° de serie" name="numero_serie" style={{ flex: 1, marginRight: 8 }}>
          <Input placeholder="Serial del fabricante" />
        </Form.Item>
        <Form.Item
          label="Registro MINVU"
          name="num_registro_minvu"
          style={{ flex: 1 }}
          tooltip="Número de registro nacional MINVU"
        >
          <Input placeholder="MINVU-12345" />
        </Form.Item>
      </Space.Compact>

      <Space.Compact block>
        <Form.Item label="Capacidad" name="capacidad" style={{ flex: 1, marginRight: 8 }}>
          <Input placeholder="8 personas" />
        </Form.Item>
        <Form.Item label="Pisos" name="pisos" style={{ flex: 1 }}>
          <Input placeholder="1-15" />
        </Form.Item>
      </Space.Compact>

      <Space.Compact block>
        <Form.Item
          label="Año fabricación"
          name="ano_fabricacion"
          style={{ flex: 1, marginRight: 8 }}
        >
          <InputNumber min={1800} max={2100} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Año instalación" name="ano_instalacion" style={{ flex: 1 }}>
          <InputNumber min={1800} max={2100} style={{ width: '100%' }} />
        </Form.Item>
      </Space.Compact>

      <Space.Compact block>
        <Form.Item
          label="Velocidad (m/min)"
          name="velocidad_mpm"
          style={{ flex: 1, marginRight: 8 }}
        >
          <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Altura recorrido (m)" name="altura_recorrido_m" style={{ flex: 1 }}>
          <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
        </Form.Item>
      </Space.Compact>

      <Form.Item label="Fecha recepción DOM" name="fecha_recepcion_dom">
        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
      </Form.Item>

      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={submitting}
        block
      >
        {isCreate ? 'Crear equipo' : 'Guardar cambios'}
      </Button>
    </Form>
  );
}

export default EquipoDatosForm;
